import React from 'react';

import { Link } from 'react-router';

import { FolderChecked } from 'icons';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, Tooltip, Typography } from '@cfa/react-components';

import { formatDate } from '@/utils/date';
import { getProgressBarText } from '@/utils/generateProgressBarText';
import { generateProgressText } from '@/utils/generateProgressText';
import { cn } from '@/utils/tailwind';
import { useRouteMatcher } from '@/utils/url';

import PlanCardSubHeader from '@/components/Cards/PlanCard/PlanCardComponents/PlanCardSubHeader';
import ProgressBar from '@/components/ProgressBar/ProgressBar';

import Constants, { routes } from '@/constants';

import Badge from '../Badge';

import ManageMenu from './components/ManageMenu';

interface BasePlanVariantProps {
  id: string;
  planId?: string;
  name: string;
  simpleUserName: string;
  isComplete: boolean;
  isReports?: boolean;
  isReportsPlansView?: boolean;
  completedStepCount: number;
  completionDate?: string | null;
  expirationDate?: string;
  isFailed?: boolean;
  isFoodSafety?: boolean;
  percentComplete?: number;
  previouslyCompletedPlanAssignmentDate?: string;
  selectedDueDate?: number;
  startDate?: string;
  stepCount: number;
  timeSpentOnPlan?: string | null;
  handleUnassign: () => void;
  handleUpdateDueDate: () => void;
  handleMarkComplete: () => void;
  handleRestartTraining: () => void;
  userAllowedToUpdateTMDueDateOnPlan?: boolean;
  userCanMarkAsComplete?: boolean;
}

const BasePlanVariant: React.FC<BasePlanVariantProps> = ({
  id,
  planId,
  name,
  simpleUserName,
  isComplete,
  isReports = false,
  isReportsPlansView = false,
  stepCount,
  completionDate,
  isFailed,
  isFoodSafety,
  completedStepCount,
  timeSpentOnPlan,
  startDate,
  selectedDueDate,
  percentComplete,
  previouslyCompletedPlanAssignmentDate,
  handleUnassign,
  handleUpdateDueDate,
  handleMarkComplete,
  handleRestartTraining,
  userAllowedToUpdateTMDueDateOnPlan,
  userCanMarkAsComplete,
}) => {
  const { t } = useTranslation();
  const routeMatches = useRouteMatcher();
  const hasPreviouslyCompletedPlanAssignment =
    !!previouslyCompletedPlanAssignmentDate;

  const isReportsPlansViewPath = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsPlansViewPath,
  );

  const isReportsTeamMembersPath = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsTeamMembersPath,
  );

  const isTrainingTeamMembersPath = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingTeamMembersPath,
  );

  const isCompleteAndReports = isComplete && isReports;
  const progressBarText = getProgressBarText({
    isReports,
    isComplete,
    completedStepCount,
    stepCount,
    timeSpentOnPlan,
    isCompliance: false,
    percentComplete,
  });

  return (
    <>
      <Card className="relative h-auto animate-[fadeIn_linear_0.3s] flex-row overflow-visible p-0!">
        <CardContent className="flex flex-col p-4!">
          <>
            <div className="flex w-full flex-row items-center justify-between gap-4!">
              <Link
                className="flex w-full min-w-0 cursor-pointer flex-wrap items-start justify-start rounded-none border-none bg-transparent font-medium normal-case no-underline sm:flex-row sm:items-center"
                to={
                  // Matches /reports/team-members
                  isReportsTeamMembersPath
                    ? routes.reports.teamMemberProgress({ teamMemberId: id })
                    : // Matches /reports/plans
                      isReportsPlansViewPath && planId
                      ? routes.reports.teamMemberPlanProgress({
                          planId,
                          teamMemberId: id,
                        })
                      : // Matches /plans/manage-plans/:planId
                        isTrainingTeamMembersPath && planId
                        ? routes.plans.managePlanTeamMembers({
                            teamMemberId: id,
                            planId,
                          })
                        : '/'
                }
              >
                <div
                  className="mx-0 flex max-w-full grow flex-col items-start justify-start overflow-hidden sm:mx-4"
                  data-simpleusername={simpleUserName}
                >
                  <Typography
                    data-testid="PlanName"
                    fontWeight="bold"
                    variant="body1"
                    className="line-clamp-2 max-w-full min-w-0 overflow-hidden text-ellipsis"
                  >
                    {name}
                  </Typography>
                  {isCompleteAndReports ? (
                    <>
                      <PlanCardSubHeader
                        className="success"
                        data-testid="PlanTaskCount"
                      >
                        {generateProgressText({
                          completedStepCount,
                          isCompliance: false,
                          isComplete,
                          isFailed,
                          isFoodSafety,
                          timeSpentOnPlan: timeSpentOnPlan,
                          stepCount,
                        })}
                      </PlanCardSubHeader>
                      {!!startDate && (
                        <PlanCardSubHeader data-testid="PlanTaskCount">
                          {`${t('Reports.filtering.startDate')}: ${formatDate(startDate)}`}
                        </PlanCardSubHeader>
                      )}
                    </>
                  ) : (
                    <div className="flex w-full justify-between">
                      {isReports ? (
                        <div className="text-left">
                          <Typography
                            className="whitespace-nowrap"
                            variant="body2"
                            data-testid="ProgressBarText"
                          >
                            {progressBarText}
                          </Typography>
                          {!!startDate && (
                            <Typography variant="body2">
                              {`${t('Reports.filtering.startDate')}: ${formatDate(startDate)}`}
                            </Typography>
                          )}
                        </div>
                      ) : (
                        <p
                          className={cn(
                            isComplete && 'text-brand-green! font-bold',
                            'text-variant-body2 whitespace-nowrap',
                          )}
                          data-testid="ProgressBarText"
                        >
                          {progressBarText}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="mt-2 flex w-full shrink-0 items-center justify-start gap-2 pb-0 sm:mt-0 sm:w-auto sm:justify-center sm:gap-4">
                  {hasPreviouslyCompletedPlanAssignment && !isComplete && (
                    <Tooltip
                      content={
                        <p className="text-variant-body2 text-grayscale-gray7 font-normal">
                          {t('TrainingPlans.statusOptions.lastCompleted')}{' '}
                          {formatDate(previouslyCompletedPlanAssignmentDate)}
                        </p>
                      }
                      placement="top"
                      showOnElementEvents={['hover']}
                    >
                      <FolderChecked className="size-6" />
                    </Tooltip>
                  )}
                  <Badge
                    isComplete={isComplete}
                    completionDate={completionDate}
                    selectedDueDate={selectedDueDate ?? 0}
                    showDate
                  />
                </div>
              </Link>
              <ManageMenu
                id={id}
                name={name}
                isComplete={isComplete}
                isReports={isReports}
                isReportsPlansView={isReportsPlansView}
                completedStepCount={completedStepCount}
                handleRestartTraining={handleRestartTraining}
                handleUnassign={handleUnassign}
                handleUpdateDueDate={handleUpdateDueDate}
                handleMarkComplete={handleMarkComplete}
                userAllowedToUpdateTMDueDateOnPlan={
                  userAllowedToUpdateTMDueDateOnPlan
                }
                userCanMarkAsComplete={userCanMarkAsComplete}
              />
            </div>
            {!isCompleteAndReports && (
              <div className="ml-0 sm:ml-4">
                <ProgressBar
                  current={completedStepCount}
                  isComplete={isComplete}
                  isReports={isReports}
                  text=""
                  total={stepCount}
                />
              </div>
            )}
          </>
        </CardContent>
      </Card>
    </>
  );
};

export default BasePlanVariant;
